import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create axios instance with default config
const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests if available
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('conti_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth API
export const authAPI = {
  register: async (userData) => {
    const response = await api.post('/auth/register', userData);
    return response.data;
  },
  
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },
  
  getMe: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
};

// Properties API
export const propertiesAPI = {
  getAll: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value && value !== 'any' && value !== 'all' && value !== 'none') {
        params.set(key, value);
      }
    });
    const response = await api.get(`/properties?${params.toString()}`);
    return response.data;
  },
  
  getFeatured: async () => {
    const response = await api.get('/properties/featured');
    return response.data;
  },
  
  getById: async (id) => {
    const response = await api.get(`/properties/${id}`);
    return response.data;
  },
};

// DDF/MLS Live Listings API
export const ddfAPI = {
  testConnection: async () => {
    const response = await api.get('/ddf/test');
    return response.data;
  },
  
  getProperties: async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.city) params.set('city', filters.city);
    if (filters.search) params.set('search', filters.search);
    if (filters.minPrice) params.set('min_price', filters.minPrice);
    if (filters.maxPrice) params.set('max_price', filters.maxPrice);
    if (filters.beds && filters.beds !== 'any') params.set('beds', filters.beds);
    if (filters.baths && filters.baths !== 'any') params.set('baths', filters.baths);
    if (filters.propertyType && filters.propertyType !== 'all') params.set('property_type', filters.propertyType);
    if (filters.limit) params.set('limit', filters.limit);
    if (filters.skip) params.set('skip', filters.skip);
    
    const response = await api.get(`/ddf/properties?${params.toString()}`);
    return response.data;
  },
  
  getFeatured: async () => {
    const response = await api.get('/ddf/properties/featured');
    return response.data;
  },
  
  getMyListings: async () => {
    const response = await api.get('/ddf/properties/mine');
    return response.data;
  },

  getLuxury: async () => {
    const response = await api.get('/ddf/properties/luxury');
    return response.data;
  },
  
  getById: async (listingKey) => {
    const response = await api.get(`/ddf/properties/${listingKey}`);
    return response.data;
  },
  
  search: async (areas, filters = {}) => {
    const params = new URLSearchParams();
    if (areas && areas.length > 0) params.set('areas', areas.join(','));
    if (filters.minPrice) params.set('min_price', filters.minPrice);
    if (filters.maxPrice) params.set('max_price', filters.maxPrice);
    if (filters.beds && filters.beds !== 'any') params.set('beds', filters.beds);
    if (filters.baths && filters.baths !== 'any') params.set('baths', filters.baths);
    if (filters.limit) params.set('limit', filters.limit);
    
    const response = await api.get(`/ddf/search?${params.toString()}`);
    return response.data;
  },
  
  getMarketStats: async () => {
    const response = await api.get('/ddf/market-stats');
    return response.data;
  },
  
  getCityMarketStats: async (city) => {
    const response = await api.get(`/ddf/market-stats/${city}`);
    return response.data;
  },
  
  getRecentlySold: async (city = null, limit = 50) => {
    const params = new URLSearchParams();
    if (city) params.set('city', city);
    params.set('limit', limit);
    const response = await api.get(`/ddf/recently-sold?${params.toString()}`);
    return response.data;
  },
};

// Neighborhoods API
export const neighborhoodsAPI = {
  getAll: async () => {
    const response = await api.get('/neighborhoods');
    return response.data;
  },
  
  getById: async (id) => {
    const response = await api.get(`/neighborhoods/${id}`);
    return response.data;
  },
  
  getStats: async (id) => {
    const response = await api.get(`/neighborhoods/${id}/stats`);
    return response.data;
  },
};

// New Builds API
export const newBuildsAPI = {
  getAll: async () => {
    const response = await api.get('/new-builds');
    return response.data;
  },
  
  getById: async (id) => {
    const response = await api.get(`/new-builds/${id}`);
    return response.data;
  },
};

// Leads API
export const leadsAPI = {
  create: async (leadData) => {
    const response = await api.post('/leads/', leadData);
    return response.data;
  },
  submit: async (leadData) => {
    const response = await api.post('/leads/', leadData);
    return response.data;
  },
};

// User API (favorites, saved searches)
export const userAPI = {
  getFavorites: async () => {
    const response = await api.get('/users/favorites');
    return response.data;
  },
  
  addFavorite: async (propertyId) => {
    const response = await api.post(`/users/favorites/${propertyId}`);
    return response.data;
  },
  
  removeFavorite: async (propertyId) => {
    const response = await api.delete(`/users/favorites/${propertyId}`);
    return response.data;
  },
  
  getSavedSearches: async () => {
    const response = await api.get('/users/searches');
    return response.data;
  },
  
  saveSearch: async (searchData) => {
    const response = await api.post('/users/searches', searchData);
    return response.data;
  },
  
  updateSearch: async (searchId, updates) => {
    const response = await api.patch(`/users/searches/${searchId}`, updates);
    return response.data;
  },
  
  deleteSearch: async (searchId) => {
    const response = await api.delete(`/users/searches/${searchId}`);
    return response.data;
  },
};

// Email Alerts API
export const alertsAPI = {
  getStatus: async () => {
    const response = await api.get('/alerts/status');
    return response.data;
  },
  
  sendTestAlert: async () => {
    const response = await api.post('/alerts/test');
    return response.data;
  },
};

// AI Sales Agent (admin) API
export const agentAPI = {
  getStats: async () => (await api.get('/agent/stats')).data,
  listLeads: async (q = '') => (await api.get(`/agent/leads${q ? `?q=${encodeURIComponent(q)}` : ''}`)).data,
  getLead: async (leadId) => (await api.get(`/agent/leads/${leadId}`)).data,
  createLead: async (lead) => (await api.post('/agent/leads', lead)).data,
  deleteLead: async (leadId) => (await api.delete(`/agent/leads/${leadId}`)).data,
  bulkDeleteLeads: async (leadIds) => (await api.post('/agent/leads/bulk-delete', { lead_ids: leadIds })).data,
  addNote: async (leadId, body) => (await api.post(`/agent/leads/${leadId}/activities`, { type: 'note', body, direction: 'system' })).data,
  draftEmail: async (leadId, instruction = '') => (await api.post(`/agent/leads/${leadId}/ai/draft-email`, { instruction })).data,
  draftSms: async (leadId, instruction = '') => (await api.post(`/agent/leads/${leadId}/ai/draft-sms`, { instruction })).data,
  callScript: async (leadId) => (await api.post(`/agent/leads/${leadId}/ai/call-script`)).data,
  sendEmail: async (leadId, subject, body) => (await api.post(`/agent/leads/${leadId}/send-email`, { subject, body })).data,
  sendSms: async (leadId, body) => (await api.post(`/agent/leads/${leadId}/send-sms`, { body })).data,
  cancelDraft: async (activityId) => (await api.delete(`/agent/activities/${activityId}`)).data,
  // Voice
  startCall: async (leadId) => (await api.post(`/agent/leads/${leadId}/call`)).data,
  warmTransfer: async (callId) => (await api.post(`/agent/calls/${callId}/warm-transfer`)).data,
  getCall: async (callId) => (await api.get(`/agent/calls/${callId}`)).data,
  rescoreCall: async (callId) => (await api.post(`/agent/calls/${callId}/rescore`)).data,
  // One-click pre-deploy smoke test: simulates a 5-turn AI voice conversation
  // server-side (no Twilio cost, no real lead pinged) and returns PASS/FAIL.
  dryRunVoice: async () => (await api.post('/agent/voice/dry-run')).data,
  // Nurture management
  getNurture: async (leadId) => (await api.get(`/agent/leads/${leadId}/nurture`)).data,
  startNurture: async (leadId) => (await api.post(`/agent/leads/${leadId}/nurture/start`)).data,
  stopNurture: async (leadId) => (await api.post(`/agent/leads/${leadId}/nurture/stop`)).data,
  stopNurtureByContact: async (email, phone, reason = 'manual_stop') =>
    (await api.post('/agent/nurture/stop-by-contact', { email, phone, reason })).data,
  // Per-lead listing-alerts opt-in/out toggle
  setListingAlerts: async (leadId, enabled) =>
    (await api.patch(`/agent/leads/${leadId}/listing-alerts`, { listing_alerts_enabled: !!enabled })).data,
  // AI master kill-switch (back office)
  getAiSettings: async () => (await api.get('/agent/settings/ai')).data,
  updateAiSettings: async (payload) => (await api.put('/agent/settings/ai', payload)).data,
};

// Google Calendar API
export const gcalAPI = {
  getStatus: async () => (await api.get('/gcal/status')).data,
  getAuthUrl: async () => (await api.get('/gcal/auth-url')).data,
  disconnect: async () => (await api.post('/gcal/disconnect')).data,
  createEvent: async (payload) => (await api.post('/gcal/events', payload)).data,
  listEvents: async (days_ahead = 14) => (await api.get(`/gcal/events?days_ahead=${days_ahead}`)).data,
};

// Live Chat (public widget) API
export const chatAPI = {
  sendMessage: async (sessionId, message, visitorInfo = {}) => {
    const res = await api.post('/chat/message', { session_id: sessionId, message, ...visitorInfo });
    return res.data;
  },
  handoff: async (sessionId, visitor) => {
    const res = await api.post('/chat/handoff', { session_id: sessionId, ...visitor });
    return res.data;
  },
  getSession: async (sessionId) => (await api.get(`/chat/session/${sessionId}`)).data,
};

export default api;
